import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, BadgeCheck, Zap } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setTx(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= max) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= max) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sid]);

  const isVerified = tx?.kind === "verified";
  const isBriefFeatured = tx?.kind === "brief_featured";

  return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      {status === "pending" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-forest" />
          <h1 className="mt-6 font-display font-black text-4xl tracking-tighter text-forest">Confirming payment…</h1>
          <p className="mt-2 text-muted-foreground">One moment while we verify with Stripe.</p>
        </>
      )}
      {status === "paid" && (
        <>
          {isVerified ? (
            <>
              <BadgeCheck className="w-20 h-20 mx-auto text-forest" />
              <h1 className="mt-6 font-display font-black text-4xl tracking-tighter text-forest" data-testid="payment-success-heading">You&apos;re Verified ✓</h1>
              <p className="mt-3 text-muted-foreground">Your Verified badge is now live on every listing, request and profile.</p>
            </>
          ) : isBriefFeatured ? (
            <>
              <Zap className="w-16 h-16 mx-auto text-safety" />
              <h1 className="mt-6 font-display font-black text-4xl tracking-tighter text-forest" data-testid="payment-success-heading">Request boosted ⚡</h1>
              <p className="mt-3 text-muted-foreground">Your clearance request is now pinned to the top. Expect quotes to roll in fast.</p>
            </>
          ) : (
            <>
              <Zap className="w-16 h-16 mx-auto text-forest" />
              <h1 className="mt-6 font-display font-black text-4xl tracking-tighter text-forest" data-testid="payment-success-heading">Boost activated 🎉</h1>
              <p className="mt-3 text-muted-foreground">Your listing is now featured. Buyers will see it front and centre.</p>
            </>
          )}
          {tx && (
            <div className="mt-6 inline-block border border-border bg-white px-6 py-4 text-left">
              <div className="overline text-muted-foreground">Amount</div>
              <div className="font-display text-2xl tabular-nums">£{Number(tx.amount).toFixed(2)}</div>
            </div>
          )}
          <div className="mt-8 flex gap-3 justify-center">
            <Button asChild className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8">
              <Link to={isVerified ? "/dashboard/verification" : isBriefFeatured ? "/dashboard/briefs" : "/dashboard/listings"}>
                {isVerified ? "Back to verification" : isBriefFeatured ? "Back to my requests" : "Back to listings"}
              </Link>
            </Button>
          </div>
        </>
      )}
      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-16 h-16 mx-auto text-safety" />
          <h1 className="mt-6 font-display font-black text-4xl tracking-tighter text-forest">Payment not completed</h1>
          <p className="mt-2 text-muted-foreground">
            {status === "expired" ? "The session expired." : "We couldn't confirm your payment."}
          </p>
          <Button asChild className="mt-6 rounded-none bg-safety hover:bg-safety-hover text-white h-12 px-8">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </>
      )}
    </div>
  );
}
